import React from 'react';

import { Select } from '@aline/common';
import { MoveDown } from 'lucide-react';
import { makeStyles } from 'tss-react/mui';

import type { SuggestionStop } from './suggestion-model';
import { CURRENT_STOP_TONE, SUGGESTION_TONES } from './suggestion-tones';
import { SuggestionActions } from './SuggestionActions';
import { SuggestionRationaleTooltip } from './SuggestionRationaleTooltip';
import { useMoveHover, useStopDecision } from './SuggestionReviewContext';

const useStyles = makeStyles<{ current: boolean }>()((theme, { current }) => ({
  label: {
    alignItems: 'center',
    display: 'flex',
    flexGrow: 1,
    gap: theme.spacing(0.5),
    minWidth: 0,
  },
  strip: {
    alignItems: 'center',
    backgroundColor: SUGGESTION_TONES.change.background,
    borderTop: `1px dashed ${current ? CURRENT_STOP_TONE.color : SUGGESTION_TONES.change.border}`,
    color: SUGGESTION_TONES.change.color,
    display: 'flex',
    fontSize: 12,
    fontWeight: 500,
    gap: theme.spacing(1),
    padding: theme.spacing(0.25, 1),
  },
}));

interface MoveSuggestionStripProps {
  current: boolean;
  /** The move's stop. Its entity id is what the arrow overlay pairs this end with its ghost by. */
  stop: SuggestionStop;
  label?: string;
}

/** The source end of a pending move: a strip along the bottom edge carrying the move's ✕ / ✓. */
export const MoveSuggestionStrip = React.memo(function MoveSuggestionStrip({
  current,
  label = 'Move suggestion',
  stop,
}: MoveSuggestionStripProps): JSX.Element {
  const { classes } = useStyles({ current });
  const decision = useStopDecision(stop);
  const hover = useMoveHover(stop.entityId);

  return (
    <div
      className={classes.strip}
      data-cy={Select.playbooks.suggestionMoveStrip}
      data-move-anchor={`${stop.entityId}:source`}
      onClick={decision.onSelect}
      onMouseEnter={hover.onMouseEnter}
      onMouseLeave={hover.onMouseLeave}
    >
      <SuggestionRationaleTooltip rationales={[stop.rationale]}>
        <span className={classes.label}>
          <MoveDown size={12} />
          {label}
        </span>
      </SuggestionRationaleTooltip>
      <SuggestionActions
        acceptDisabledReason={stop.blockedReason}
        acceptTitle="Accept move"
        rejectTitle="Reject move"
        size={14}
        onAccept={decision.onAccept}
        onReject={decision.onReject}
      />
    </div>
  );
});
